/**
 * @file content-block.schema.ts
 * @description Define os schemas Zod para os diferentes tipos de blocos de conteúdo
 * que podem ser usados nas entidades Magazine e PartnerContent.
 */

import { z } from 'zod';

// --- Base Schema e Enums ---

/**
 * Um schema base que inclui o campo de ordenação 'index',
 * comum a todos os tipos de bloco.
 * O campo index é opcional para compatibilidade com dados existentes.
 */
const BaseBlockSchema = z.object({
  index: z.number().int().optional(),
});

const MagazineSectionNameEnum = z.enum([
  'INTRO', 'ABOUT_BABY', 'ABOUT_MOTHER', 'WELL_BEING', 'BOUND',
]);


// --- Schemas de Bloco Específicos ---

const MagazineSectionBlockSchema = BaseBlockSchema.extend({
  type: z.literal('MAGAZINE_SECTION'),
  sectionName: MagazineSectionNameEnum.optional(), // Campo legado para compatibilidade
  categoryId: z.string().optional(), // Opcional para permitir criação inicial, mas deve ser preenchido
});

const PartnerSectionBlockSchema = BaseBlockSchema.extend({
  type: z.literal('PARTNER_SECTION'),
  title: z.string(),
  iconUrl: z.string().url(),
});

const ParagraphBlockSchema = BaseBlockSchema.extend({
  type: z.literal('PARAGRAPH'),
  text: z.string(),
});

const ImageParagraphBlockSchema = BaseBlockSchema.extend({
  type: z.literal('IMAGE_PARAGRAPH'),
  text: z.string(),
  imageUrl: z.string().url().or(z.literal('')), // Permite URL válida ou string vazia
  imagePosition: z.enum(['left', 'right']),
});

const ImageOnlyBlockSchema = BaseBlockSchema.extend({
  type: z.literal('IMAGE_ONLY'),
  imageUrl: z.string().url().or(z.literal('')), // Permite URL válida ou string vazia
});

const CourseClassReferenceSchema = z.object({
  courseId: z.string(),
  moduleIndex: z.number().int().min(0),
  classIndex: z.number().int().min(0), // Índice do vídeo dentro do módulo
  orderIndex: z.number().int().min(0), // Posição no slider da revista
});

const CourseClassesBlockSchema = BaseBlockSchema.extend({
  type: z.literal('COURSE_CLASSES'),
  classes: z.array(CourseClassReferenceSchema),
});

const PartnerCommentBlockSchema = BaseBlockSchema.extend({
  type: z.literal('PARTNER_COMMENT'),
  title: z.string().optional(),
  text: z.string(),
});


// --- Schema Final de Bloco de Conteúdo ---

/**
 * O schema final para um Bloco de Conteúdo, unindo todas as possibilidades.
 */
const ContentBlockSchema = z.discriminatedUnion('type', [
  MagazineSectionBlockSchema,
  PartnerSectionBlockSchema,
  PartnerCommentBlockSchema,
  ParagraphBlockSchema,
  ImageParagraphBlockSchema,
  ImageOnlyBlockSchema,
  CourseClassesBlockSchema,
]);


// --- Inferred TypeScript Types ---

type ContentBlock = z.infer<typeof ContentBlockSchema>;
type MagazineSectionName = z.infer<typeof MagazineSectionNameEnum>;
type CourseClassReference = z.infer<typeof CourseClassReferenceSchema>;


// --- Public Exports ---

export { ContentBlockSchema, MagazineSectionNameEnum, CourseClassReferenceSchema };
export type { ContentBlock, MagazineSectionName, CourseClassReference };